import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const ClientDataTable = ({
  clients,
  onStatusToggle,
  onAction,
  sortConfig,
  onSort
}) => {
  const navigate = useNavigate();

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    }

    return sortConfig?.direction === 'asc'
      ? <Icon name="ChevronUp" size={14} className="text-primary" />
      : <Icon name="ChevronDown" size={14} className="text-primary" />;
  };

  const handleViewClient = (clientId) => {
    navigate(`/client-profile-details?id=${clientId}`);
  };

  return (
    <div className="bg-card border border-border rounded-lg elevation-sm overflow-hidden">
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('ownerName')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
                >
                  Name
                  <SortIcon columnKey="ownerName" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Mobile No
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Email
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('companyName')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
                >
                  Company Name
                  <SortIcon columnKey="companyName" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('businessType')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
                >
                  Business Type
                  <SortIcon columnKey="businessType" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('createdDate')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
                >
                  Created Date
                  <SortIcon columnKey="createdDate" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('lastActiveDate')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
                >
                  Last Date
                  <SortIcon columnKey="lastActiveDate" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Status
                </span>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Action
                </span>
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {clients?.map((client) => (
              <tr key={client?.id} className="hover:bg-muted/30 transition-smooth">
                <td className="px-4 py-3 text-sm text-foreground">{client?.ownerName}</td>
                <td className="px-4 py-3 text-sm text-foreground">{client?.mobile}</td>
                <td className="px-4 py-3 text-sm text-foreground">{client?.email}</td>
                <td className="px-4 py-3 text-sm text-foreground">{client?.companyName}</td>
                <td className="px-4 py-3 text-sm text-foreground">{client?.businessType}</td>
                <td className="px-4 py-3 text-sm text-foreground">{client?.createdDate}</td>
                <td className="px-4 py-3 text-sm text-foreground">{client?.lastActiveDate}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() =>
                      onStatusToggle(client, client?.status === 'active' ? 'inactive' : 'active')
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      client?.status === 'active' ? 'bg-success' : 'bg-muted'
                    }`}
                    title={client?.status === 'active' ? 'Deactivate' : 'Activate'}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        client?.status === 'active' ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleViewClient(client?.id)}
                      className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                      title="View Details"
                    >
                      <Icon name="Eye" size={16} className="text-primary" />
                    </button>
                    <button
                      onClick={() => onAction('delete', client)}
                      className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                      title="Delete"
                    >
                      <Icon name="Trash2" size={16} className="text-destructive" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="lg:hidden divide-y divide-border">
        {clients?.map((client) => (
          <div key={client?.id} className="p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">{client?.ownerName}</p>
                <p className="text-xs text-muted-foreground mt-1">{client?.companyName}</p>
              </div>

              <button
                onClick={() =>
                  onStatusToggle(client, client?.status === 'active' ? 'inactive' : 'active')
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  client?.status === 'active' ? 'bg-success' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    client?.status === 'active' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="text-xs text-muted-foreground">Mobile: {client?.mobile}</div>
            <div className="text-xs text-muted-foreground">Email: {client?.email}</div>
            <div className="text-xs text-muted-foreground">Business Type: {client?.businessType}</div>
            <div className="text-xs text-muted-foreground">Created: {client?.createdDate}</div>
            <div className="text-xs text-muted-foreground">Last Date: {client?.lastActiveDate}</div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <button
                onClick={() => handleViewClient(client?.id)}
                className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                title="View Details"
              >
                <Icon name="Eye" size={16} className="text-primary" />
              </button>
              <button
                onClick={() => onAction('delete', client)}
                className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                title="Delete"
              >
                <Icon name="Trash2" size={16} className="text-destructive" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {clients?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">No clients found</p>
        </div>
      )}
    </div>
  );
};

export default ClientDataTable;